import { useState } from 'react';
import { BatteryLow, Loader2, MessageSquare, ShieldAlert } from 'lucide-react';
import { agenticAPI, pentestsAPI } from '../../../../services/api';
import { LEVELS } from '../../ScopeTargetLevels';

/**
 * The filled button for granting a level, one shade per level.
 *
 * The chip colours come from LEVELS, which Pre-Engagement and the plan screen
 * already use, so a level looks the same everywhere it appears. These are the
 * ACTION variants of the same three hues: a solid fill rather than a tint,
 * because this button changes what an agent may do to a live host and should
 * not look like a label.
 *
 * Gravity escalates with solidity, deliberately. Granting Exploit permits
 * command execution, credential brute force, destructive SQL and filesystem
 * writes with no further gate, so it gets the red that makes an operator pause.
 * red-600 under white text clears contrast at button size; the red-50 tint the
 * chip uses would not carry the weight.
 */
const GRANT_FILL = {
  confirm: 'bg-wasabi-600 hover:bg-wasabi-700',
  prove: 'bg-amber-600 hover:bg-amber-700',
  exploit: 'bg-red-600 hover:bg-red-700',
};

/**
 * One place decides what a level is called and coloured, and it is not here.
 * Falls back to Confirm rather than rendering an empty chip, so an unknown
 * value from an older row still reads as something.
 */
const level = (value) => LEVELS.find((l) => l.value === value) || LEVELS[0];

/**
 * The decision dock.
 *
 * Pinned rather than a tab, because an unanswered authority raise stalls a hypothesis
 * and a parked run spends nothing while it waits. Something the operator must
 * answer should never be behind a tab they have to remember to open.
 *
 * Clicking an item selects the hypothesis that raised it, so the answer is given
 * while looking at the evidence that justifies it rather than from a list of
 * abstract requests.
 */

export default function DecisionDock({
  pentestId, overview, onSelectHypothesis, onOpenPlan, onChanged,
}) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  //: Collapsed by default so the bar is one line until the operator wants the
  //: detail. NOT a dismiss: the X that used to sit here hid the only control
  //: that unblocks a stopped engagement, and bought three seconds, because the
  //: page polls and it came straight back.
  const [open, setOpen] = useState(false);
  //: Run id whose grant needs a written reason before it can go through, and
  //: the reason being typed. Set only when the backend asks for one, which it
  //: does with a 400 rather than a 403: incident validation on production is
  //: authorised recurring work, and a system that refuses it outright gets
  //: bypassed with a shell script, after which nothing is recorded at all.
  const [needsReason, setNeedsReason] = useState(null);
  const [reason, setReason] = useState('');

  // A PLAN PARK IS NOT A DOCK ITEM. The dock exists so a decision is never
  // hidden behind a tab, and plan approval is the one decision that already has
  // a home: the Plan tab, with the plan in front of you and Approve and Send
  // back beneath it. Carrying it here produced four separate things saying
  // "plan v2 needs you" on one screen, and a "Review and decide" button that
  // did nothing at all once you were on the plan tab, because the thing it
  // pointed at was already under the cursor.
  //
  // Identified by the ABSENCE of an asked level, the same way _with_ask
  // distinguishes them server-side: a level request carries a target and a
  // level, a plan park carries neither.
  const planPark = (run) => run.role === 'lead' && !run.asked_level;
  const authority = (overview?.awaiting_authority || []).filter((r) => !planPark(r));
  const reported = overview?.awaiting_operator || [];
  const budget = overview?.awaiting_budget || [];
  const total = authority.length + reported.length + budget.length;

  if (!total) return null;

  // Says WHAT is waiting, not just how many. A bar reading "1 decision waiting
  // on you" forces a click to discover which, and the whole point of a single
  // line is that it can be read without one.
  const headline = total > 1
    ? `${total} decisions waiting on you`
    : (reported.length
      ? 'The Lead has reported and is waiting to be read'
      : (authority.length
        ? (authority[0].asked_level
          ? `${authority[0].role === 'lead' ? 'The Lead' : 'An agent'} is asking for ${level(authority[0].asked_level).label.toLowerCase()} on ${authority[0].asked_target}`
          : 'The Lead is waiting on you')
        : 'A run is out of budget'));

  /**
   * Grant the level the agent asked for, on the target it asked about.
   *
   * Both values come from the payload, which reads them off the turn that
   * parked the run, so this cannot grant something other than what was
   * requested. Before this existed the dock said "raise the target on
   * Pre-Engagement to grant" and the operator carried a target and a level
   * across two tabs by hand.
   *
   * A missing justification comes back as a 400 asking for one. That is not an
   * error to show and stop on, it is the next step, so the reason box opens and
   * the same grant is retried with it.
   */
  const grant = async (run, authorityRef) => {
    setBusyId(run.id);
    setError(null);
    try {
      await pentestsAPI.scope.setLevel(pentestId, run.asked_target_id, {
        exploit_level: run.asked_level,
        authority_ref: authorityRef || null,
      });
      // The grant is the answer. Advancing is what makes it take effect, and an
      // answer that does not resume the thing that asked is not an answer, it
      // is a second thing to remember.
      await agenticAPI.advance(pentestId);
      setNeedsReason(null);
      setReason('');
      await onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const asksForAReason =
        e?.response?.status === 400 && /justification|reason/i.test(
          typeof detail === 'string' ? detail : JSON.stringify(detail || ''),
        );
      if (asksForAReason) {
        setNeedsReason(run.id);
        setError(typeof detail === 'string' ? detail : 'This grant needs a written reason.');
      } else {
        setError(detail || 'Could not grant the level.');
      }
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Tell the Lead its report has been read.
   *
   * The button that did not exist. `report_to_operator` parked the run on
   * awaiting_authority, which is cleared only by a plan decision or by a level
   * appearing on a scope target, and a report is neither. Pentest 36's Lead sat
   * that way for two days holding a reason that went stale in minutes.
   *
   * Carries no instruction on purpose. The Lead's next turn is assembled from
   * the ledger like every other turn, so it re-reads the engagement rather than
   * a sentence typed here.
   */
  const acknowledge = async (run) => {
    setBusyId(run.id);
    setError(null);
    try {
      await agenticAPI.acknowledgeReport(pentestId);
      await onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not acknowledge the report.');
    } finally {
      setBusyId(null);
    }
  };

  const topUp = async (run) => {
    setBusyId(run.id);
    setError(null);
    try {
      await agenticAPI.topUp(pentestId, run.id, { extra_turns: 20, extra_tokens: 100000 });
      await onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not top up the run.');
    } finally {
      setBusyId(null);
    }
  };

  // One line, pinned to the bottom of the viewport, expanding on click.
  //
  // It used to be `ml-auto w-full max-w-xl` in block flow, which reserves a
  // full-width row and then sits in the right half of it, so the page grew a
  // large empty area beside the one control that matters. Same shape as the
  // empty grid cell on the Plan tab. It is NOT going back to a tall fixed panel
  // either: that covered the roster, which is why it left fixed positioning in
  // the first place. A 56px bar does not.
  return (
    <div className="sticky bottom-0 z-20 -mx-1 rounded-xl border border-amber-300 bg-white shadow-lg">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 rounded-t-xl bg-amber-50 px-3 py-2 text-left hover:bg-amber-100"
      >
        <span className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-amber-900">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span className="truncate">{headline}</span>
        </span>
        <span className="shrink-0 text-xs font-semibold text-amber-700">
          {open ? 'Hide' : 'Answer'}
        </span>
      </button>

      {error && (
        <div className="border-b border-amber-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      <div className={`${open ? 'max-h-96' : 'max-h-0'} divide-y divide-slate-100 overflow-auto transition-[max-height]`}>
        {authority.map((run) => {
          // A lead run has no hypothesis, so there is no evidence pane to send
          // the operator to. It parks on a plan or on a raise it asked for on a
          // worker's behalf, and the plan is where both are answered.
          const isLead = run.role === 'lead';
          return (
            <div key={`a-${run.id}`} className="p-3">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-800">
                <ShieldAlert className="h-3.5 w-3.5" />
                {!isLead ? 'Authority raise' : 'The Lead is asking for a level'}
              </div>
              <p className="mb-2 text-xs leading-snug text-slate-700">
                {run.blocked_reason
                  || (isLead
                    ? 'The Lead has drafted a plan and stopped for your decision.'
                    : 'The agent has reached its level ceiling.')}
              </p>
              {/* The ask, when this park IS a level request. A plan park and an
                  operator report arrive in this same list and carry no
                  asked_level, so they keep the review button alone: granting a
                  level would not answer either of them. */}
              {run.asked_level && (
                <div className="mb-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
                  <div className="mb-1 flex items-center gap-1.5">
                    <span
                      className={`rounded border px-1.5 py-0.5 text-[11px] font-semibold ${
                        level(run.asked_level).chip
                      }`}
                    >
                      {level(run.asked_level).label}
                    </span>
                    <span className="truncate text-xs font-medium text-slate-700">
                      {run.asked_target}
                    </span>
                  </div>
                  <p className="text-[11px] leading-snug text-slate-500">
                    {level(run.asked_level).blurb}
                  </p>
                  {run.asked_justification && (
                    <p className="mt-1 border-l-2 border-slate-300 pl-2 text-[11px] italic leading-snug text-slate-600">
                      {run.asked_justification}
                    </p>
                  )}
                </div>
              )}

              {needsReason === run.id && (
                <div className="mb-2">
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={2}
                    placeholder="Who authorised this, and why. This sentence goes in the report."
                    className="w-full rounded-md border border-amber-300 px-2 py-1 text-xs"
                  />
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {/* Granted where it was asked. The level and the target are the
                    agent's own, read from the turn that parked it. */}
                {run.asked_level && run.asked_target_id && (
                  <button
                    type="button"
                    disabled={busyId === run.id || (needsReason === run.id && !reason.trim())}
                    onClick={() => grant(run, needsReason === run.id ? reason.trim() : null)}
                    className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50 ${
                      GRANT_FILL[run.asked_level] || GRANT_FILL.confirm
                    }`}
                  >
                    {busyId === run.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Grant {level(run.asked_level).label.toLowerCase()} on {run.asked_target}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => (isLead ? onOpenPlan?.() : onSelectHypothesis?.(run.hypothesis_id))}
                  className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {isLead ? 'Open the plan' : 'Review the evidence'}
                </button>
                <span className="text-[11px] text-slate-400">
                  {run.asked_level
                    ? (run.asked_target_id
                      ? 'nothing else changes: this raises one target'
                      : `${run.asked_target} is no longer in scope, so there is nothing to raise`)
                    : 'nothing touches a host until you answer'}
                </span>
              </div>
            </div>
          );
        })}

        {reported.map((run) => (
          <div key={`r-${run.id}`} className="p-3">
            <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-800">
              <MessageSquare className="h-3.5 w-3.5" />
              The Lead has something to tell you
            </div>
            <p className="mb-2 whitespace-pre-line text-xs leading-snug text-slate-700">
              {run.blocked_reason || 'The Lead stopped to report to you.'}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={busyId === run.id}
                onClick={() => acknowledge(run)}
                className="inline-flex items-center gap-1.5 rounded-md bg-wasabi-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-wasabi-700 disabled:opacity-50"
              >
                {busyId === run.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Got it, carry on
              </button>
              <button
                type="button"
                onClick={() => onOpenPlan?.()}
                className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Open the plan
              </button>
              {/* Said plainly because the report often reads like a question and
                  is not one. The Lead re-reads the engagement from the ledger on
                  its next turn, so whatever made it stop is either still true,
                  in which case it reports again, or it is not. */}
              <span className="text-[11px] text-slate-400">
                this only says you have read it; the Lead decides what to do next
              </span>
            </div>
          </div>
        ))}

        {budget.map((run) => (
          <div key={`b-${run.id}`} className="p-3">
            <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-slate-700">
              <BatteryLow className="h-3.5 w-3.5" /> Budget exhausted
            </div>
            <p className="mb-2 text-xs leading-snug text-slate-600">
              {run.blocked_reason || 'The run reached its cap.'}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={busyId === run.id}
                onClick={() => topUp(run)}
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {busyId === run.id && <Loader2 className="h-3 w-3 animate-spin" />}
                Add 20 turns
              </button>
              <button
                type="button"
                onClick={() => onSelectHypothesis?.(run.hypothesis_id)}
                className="text-xs font-medium text-wasabi-700 hover:text-wasabi-800"
              >
                Look first
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
